import { Box, CircularProgress } from '@material-ui/core';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { DataGrid, DataGridProps, GridCellParams, GridColDef, GridRowData, MuiEvent } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';

import { ListFilter } from '@/components/modules/CheckoutSession/List/types/ListFilter';
import { CustomPagination } from '@/components/shared/components/FetchingDataGrid/components/CustomPagination';
import { CustomToolbar } from '@/components/shared/components/FetchingDataGrid/components/CustomToolbar';
import { useFetchingDataGridStyles } from '@/components/shared/components/FetchingDataGrid/hooks/useFetchingDataGridStyles';
import { LiveUpdatesProps, useLiveList } from '@/components/shared/components/FetchingDataGrid/hooks/useLiveList';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { ListQueryProps } from '@/types/ListQueryProps';

export interface CustomGridCellParams extends Omit<GridCellParams, 'colDef'> {
  colDef: CustomGridColDef;
}

export interface CustomGridColDef extends Omit<GridColDef, 'headerName'> {
  headerName?: string | JSX.Element;
  disableOnRowClick?: boolean;
  setSearchStringOnCellClick?: (params: CustomGridCellParams) => string;
}

export type ListPayload = {
  data: GridRowData[];
  totalCount: number;
  totalPages?: number;
};

export interface FetchingDataGridProps extends Omit<DataGridProps, 'columns' | 'rows'> {
  columns: CustomGridColDef[];
  fetch: (q: ListQueryProps) => Promise<ListPayload>;
  getXLSXExport?: (query: ListQueryProps) => Promise<string>;
  defaultPageNumber?: number;
  defaultPageSize?: number;
  defaultOrderBy?: string;
  defaultOrderDirection?: ListQueryProps['orderDirection'];
  defaultSearchString?: string;
  rowsPerPageOptions?: number[];
  showSearch?: boolean;
  showExport?: boolean;
  showRefresh?: boolean;
  filters?: ListFilter[];
  liveUpdates?: LiveUpdatesProps;
  onCellClick?: (
    params: CustomGridCellParams,
    event: MuiEvent<React.MouseEvent<Element, MouseEvent>>,
    details?,
  ) => void;
  onQueryChange?: (query: ListQueryProps) => void;
  booleanFilterTools?: JSX.Element;
}

const SEARCH_TIMEOUT = 1000;
const DEFAULT_OPTIONS = {
  PAGE_NUMBER: 0,
  PAGE_SIZE: 10,
  ORDER_BY: 'created_at',
  ORDER_DIRECTION: 'desc',
  ROWS_PER_PAGE_OPTIONS: [10, 25, 50, 100],
  SHOW_SEARCH: false,
  SHOW_EXPORT: false,
  SHOW_REFRESH: false,
};

export const FetchingDataGrid = ({
  columns,
  fetch,
  getXLSXExport,
  defaultPageNumber,
  defaultPageSize,
  defaultOrderBy,
  defaultOrderDirection,
  defaultSearchString,
  rowsPerPageOptions,
  showSearch,
  showExport,
  showRefresh,
  filters = [],
  liveUpdates,
  onCellClick = () => {},
  onQueryChange = () => {},
  booleanFilterTools,
  ...props
}: FetchingDataGridProps) => {
  const { breakpoints, palette: themePalette } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const initialTableParams = {
    pageNumber: defaultPageNumber || DEFAULT_OPTIONS.PAGE_NUMBER,
    pageSize: defaultPageSize || DEFAULT_OPTIONS.PAGE_SIZE,
    orderBy: defaultOrderBy || DEFAULT_OPTIONS.ORDER_BY,
    orderDirection: defaultOrderDirection || (DEFAULT_OPTIONS.ORDER_DIRECTION as ListQueryProps['orderDirection']),
    rowsPerPageOptions: rowsPerPageOptions || DEFAULT_OPTIONS.ROWS_PER_PAGE_OPTIONS,
  };
  const [tableOptionsState, setTableOptionsState] = useState(initialTableParams);

  const [state, setState] = useState({
    data: undefined as GridRowData[] | undefined,
    totalCount: undefined as number | undefined,
    loading: true as boolean,
    filters: filters as ListFilter[],
    xlsxExportFile: undefined as string | undefined,
    exportLoading: false as boolean,
  });

  const { exportLoading } = state;

  const [searchState, setSearchState] = useState({
    searchString: defaultSearchString as string | undefined,
    lastChangeTime: undefined as Date | undefined,
    forcedTimestamp: undefined as Date | undefined,
  });

  const { searchString, lastChangeTime, forcedTimestamp } = searchState;

  const { pageNumber, pageSize, orderBy, orderDirection } = tableOptionsState;

  const { loading, data } = state;

  const { classes } = useFetchingDataGridStyles(loading);

  const setCustomSearchStringIfRequiredByColumnDef = (columnEvent: CustomGridCellParams) => {
    //on columns definition, the parent component will define a setSearchStringOnCellClick if required
    const setSearchStringOnCellClick = columnEvent.colDef.setSearchStringOnCellClick;
    if (setSearchStringOnCellClick && setSearchStringOnCellClick(columnEvent) !== null) {
      onChangeSearchText(setSearchStringOnCellClick(columnEvent));
    }
  };

  const cellClickHandler = (
    params: GridCellParams,
    event: MuiEvent<React.MouseEvent<Element, MouseEvent>>,
    details?,
  ) => {
    setCustomSearchStringIfRequiredByColumnDef(params);
    onCellClick(params, event, details);
  };

  const handleSorting = (sortParams) => {
    const params = sortParams[0];
    if (
      JSON.stringify(orderBy) !== JSON.stringify(params?.field) ||
      (JSON.stringify(orderDirection) !== JSON.stringify(params?.sort) && sortParams.length > 0)
    )
      fetchData({
        orderBy: sortParams[0]?.field,
        orderDirection: sortParams[0]?.sort,
      });
  };

  const onChangeSearchText = (searchValue: string) => {
    const timeStamp = new Date();
    setSearchState((s) => ({
      ...s,
      searchString: searchValue,
      lastChangeTime: timeStamp,
    }));
    setState((s) => ({
      ...s,
      loading: true,
    }));
    setTableOptionsState((s) => ({
      ...s,
      pageNumber: 0,
    }));
    setTimeout(() => {
      forcedCheckTrigger(timeStamp);
    }, SEARCH_TIMEOUT);
  };

  useEffect(() => {
    const currentFilters = filters || [];
    if (JSON.stringify(currentFilters) !== JSON.stringify(state.filters)) {
      setState((s) => ({
        ...s,
        filters: currentFilters,
      }));
      fetchData({ filters: currentFilters, pageNumber: 0 });
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    TriggerRequestIfTimeIsUp();
  }, [forcedTimestamp]);

  const forcedCheckTrigger = (timeStamp) => {
    setSearchState((s) => ({
      ...s,
      forcedTimestamp: timeStamp,
    }));
  };

  const TriggerRequestIfTimeIsUp = () => {
    if (!lastChangeTime) return;
    const timeDifference = new Date().getTime() - lastChangeTime.getTime();
    if (timeDifference >= SEARCH_TIMEOUT) {
      fetchData();
    }
  };

  const fetchData = (customQueryParams?: Partial<ListQueryProps>, { silent = false }: { silent?: boolean } = {}) => {
    const queryParams = {
      ...state,
      ...tableOptionsState,
      searchString: searchString || '',
      filters: state.filters,
      ...customQueryParams,
    };
    onQueryChange(queryParams);
    fetch(queryParams).then((result) => {
      setState((s) => ({
        ...s,
        data: result.data,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        loading: false,
      }));
      clearPendingChanges();
    });
    if (!silent) {
      setState((s) => ({
        ...s,
        loading: true,
      }));
    }
    setTableOptionsState((s) => ({
      ...s,
      ...customQueryParams,
    }));
  };

  const {
    state: liveState,
    pendingChanges,
    applyPendingChanges,
    clearPendingChanges,
  } = useLiveList({
    liveUpdates,
    rowIds: (data ?? []).map((row) => String(row.id)),
    refetch: () => fetchData(undefined, { silent: true }),
  });

  const fetchExportData = (customQueryParams?: Partial<ListQueryProps>) => {
    setState((s) => ({
      ...s,
      exportLoading: true,
    }));
    const queryParams = {
      ...state,
      ...tableOptionsState,
      searchString: searchString || '',
      filters: state.filters,
      ...customQueryParams,
    };
    getXLSXExport &&
      getXLSXExport(queryParams)
        .then((result) => {
          setState((s) => ({
            ...s,
            xlsxExportFile: result,
            exportLoading: false,
          }));
        })
        .catch(() => {
          setState((s) => ({
            ...s,
            xlsxExportFile: undefined,
            exportLoading: false,
          }));
        });
  };

  const handleToolbarClick = (searchValue) => {
    const refresh = () => {
      fetchData();
    };
    const fetchWithSearchValue = () => {
      setSearchState((s) => ({
        ...s,
        searchString: searchValue,
      }));
      fetchData({ searchString: searchValue });
    };
    if (searchValue === undefined) {
      refresh();
    } else {
      fetchWithSearchValue();
    }
  };

  const CustomNoRowsOverlay = () => {
    return (
      <Box display={'flex'} marginTop={'96px'} width={'100%'} justifyContent={'center'} alignItems={'center'}>
        {loading ? (
          <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
            <CircularProgress color={'secondary'} size={44} style={{ marginBottom: '16px' }} />
            <Font variant="body2" color="textSecondary">
              <Text tId={'LOADING_INFORMATION'} />
            </Font>
          </Box>
        ) : (
          <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
            <i
              className="material-icons-outlined"
              style={{
                fontSize: 28,
                paddingBottom: '8px',
                color: themePalette.grey[400],
              }}
            >
              grid_off
            </i>
            <Font variant="subtitle2" color="textSecondary">
              <Text tId={'NO_ROWS'} />
            </Font>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <DataGrid
      className={classes.root}
      autoHeight
      loading={!data || loading}
      rowHeight={74}
      disableColumnMenu
      disableColumnFilter
      columns={columns as GridColDef[]}
      paginationMode="server"
      rowCount={state?.totalCount}
      rowsPerPageOptions={[10, 25, 50, 100]}
      page={pageNumber}
      pageSize={pageSize}
      onPageChange={(pageNumber) => fetchData({ pageNumber })}
      onPageSizeChange={(pageSize) => fetchData({ pageSize })}
      sortingMode="server"
      onSortModelChange={handleSorting}
      sortModel={[{ field: orderBy, sort: orderDirection }]}
      pagination
      components={{
        Toolbar: CustomToolbar,
        Pagination: CustomPagination,
        NoRowsOverlay: CustomNoRowsOverlay,
      }}
      componentsProps={{
        pagination: {
          defaultOptions: initialTableParams,
          labelRowsPerPage: null,
          onChange: (pageSize) => fetchData({ pageSize }),
          tableState: state,
          isMobile,
        },
        toolbar: {
          value: searchString,
          onChange: (e) => onChangeSearchText(e.target.value),
          onClick: (searchSting) => handleToolbarClick(searchSting),
          onClickDownload: () => fetchExportData(),
          exportLoading: exportLoading,
          searchState: searchState,
          showSearch: showSearch,
          showExport: showExport,
          exportDisabled: !data?.length,
          showRefresh: showRefresh,
          booleanFilterTools: booleanFilterTools,
          liveState,
          pendingChanges,
          onApplyPendingChanges: applyPendingChanges,
        },
      }}
      onCellClick={cellClickHandler}
      {...props}
      rows={data || []}
    />
  );
};
